import { Route, Routes } from 'react-router-dom';
import PageTitle from './components/PageTitle';
import DefaultLayout from './layout/DefaultLayout';
import AuthLayout from './layout/AuthLayout';
import AuthProvider, { useAuth } from './utils/Auth/AuthProvider';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { NonAuthRoute } from './routes/NonAuthRoute';
import Login from './utils/Auth/login';
import Logout from './utils/Auth/logout';
import Students from './pages/Students/Students';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ExamAdd from './pages/Exams/ExamAdd';
import ExamList from './pages/Exams/ExamList';
import ExamQuestionAdd from './pages/Exams/ExamQuestionAdd';
import ExamDetail from './pages/Exams/ExamDetail';
import GenerateExam from './pages/Exams/GenerateExam';
import MixedExam from './pages/Exams/MixedExam';
import StudentProfile from './pages/Students/StudentProfile';
import StudentRequests from './pages/Students/StudentRequests';
import ForgotPassword from './utils/Auth/ForgotPassword';
import ResetPassword from './utils/Auth/ResetPassword';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminUsers from './pages/Admin/Users';
import AdminSchools from './pages/Admin/Schools';
import AdminExams from './pages/Admin/Exams';
import AdminStudentsAll from './pages/Admin/StudentsAll';
import RagSetup from './pages/Admin/RagSetup';
import RagList from './pages/Admin/RagList';
import ClassSetup from './pages/SchoolSetup/ClassSetup';
import ChapterSetup from './pages/SchoolSetup/ChapterSetup';
import TeachersSetup from './pages/SchoolSetup/Teachers';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      
    },
  },
})


function SuperOnly({ children }: { children: React.ReactNode }) {
  const { role } = useAuth();
  if (role !== 'superuser') {
    return <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">403 - Not authorized</div>;
  }
  return <>{children}</>;
}

function HomeIndex() {
  const { role } = useAuth();
  if (role === 'superuser') {
    return (
      <>
        <PageTitle title="Admin Dashboard" />
        <AdminDashboard />
      </>
    );
  }
  return (
    <>
      <PageTitle title="Students" />
      <Students />
    </>
  );
}

function App() {

  // const [loading, setLoading] = useState<boolean>(true);
  // const { pathname } = useLocation();

  // useEffect(() => {
  //   window.scrollTo(0, 0);
  // }, [pathname]);

  // useEffect(() => {
  //   setTimeout(() => setLoading(false), 1000);
  // }, []);

  // return loading ? (
  //   <Loader />
  // ) : 
  return (
    // <AuthProvider>

    //     <Routes />


    // </AuthProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Routes>
          <Route element={<ProtectedRoute />}>
            <Route element={<DefaultLayout />}>
              <Route
                index
                element={
                  <HomeIndex />
                }
              />
              <Route
                path="/students"
                element={
                  <>
                    <PageTitle title="Students" />
                    <Students />
                  </>
                }
              />
              {/* Admin routes for superuser */}
              <Route
                path="/admin"
                element={
                  <SuperOnly>
                    <PageTitle title="Admin Dashboard" />
                    <AdminDashboard />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <SuperOnly>
                    <PageTitle title="Manage Users" />
                    <AdminUsers />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/schools"
                element={
                  <SuperOnly>
                    <PageTitle title="Manage Schools" />
                    <AdminSchools />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/exams"
                element={
                  <SuperOnly>
                    <PageTitle title="Manage Exams" />
                    <AdminExams />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/students"
                element={
                  <SuperOnly>
                    <PageTitle title="Manage Students" />
                    <AdminStudentsAll />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/rag-setup"
                element={
                  <SuperOnly>
                    <PageTitle title="RAG Setup" />
                    <RagSetup />
                  </SuperOnly>
                }
              />
              <Route
                path="/admin/rag"
                element={
                  <SuperOnly>
                    <PageTitle title="RAG List" />
                    <RagList />
                  </SuperOnly>
                }
              />
              <Route
                path="/students/profle/:studId"
                element={
                  <>
                    <PageTitle title="Student Profile" />
                    <StudentProfile />
                  </>
                }
              />
              <Route
                path="/students/requests"
                element={
                  <>
                    <PageTitle title="Student Requests" />
                    <StudentRequests />
                  </>
                }
              />
              <Route
                path="/exams/exam-list"
                element={
                  <>
                    <PageTitle title="Exams" />
                    <ExamList />
                  </>
                }
              />
              <Route
                path="/exams/add-exam"
                element={
                  <>
                    <PageTitle title="Add Exam" />
                    <ExamAdd />
                  </>
                }
              />
              <Route
                path="/exams/mixed-exam"
                element={
                  <>
                    <PageTitle title="Mixed Exam" />
                    <MixedExam />
                  </>
                }
              />
              <Route
                path="/exams/add-exam/:examId"
                element={
                  <>
                    <PageTitle title="Add Exam Questions" />
                    <ExamQuestionAdd />
                  </>
                }
              />
              <Route
                path="/exams/generate"
                element={
                  <>
                    <PageTitle title="Generate Exam" />
                    <GenerateExam />
                  </>
                }
              />
              <Route
                path="/exams/exam-detail/:examId"
                element={
                  <>
                    <PageTitle title="Exam Detail" />
                    <ExamDetail />
                  </>
                }
              />
              <Route path="/logout" element={<Logout />} />

              {/* School Setup Routes */}
              <Route
                path="/school-setup/class"
                element={
                  <>
                    <PageTitle title="Class Setup" />
                    <ClassSetup />
                  </>
                }
              />
              <Route
                path="/school-setup/chapters"
                element={
                  <>
                    <PageTitle title="Chapter Setup" />
                    <ChapterSetup />
                  </>
                }
              />
              <Route
                path="/school-setup/teachers"
                element={
                  <>
                    <PageTitle title="Teachers" />
                    <TeachersSetup />
                  </>
                }
              />
            </Route>
          </Route>
          <Route element={<NonAuthRoute />}>
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route
                path="/auth/forgot-password"
                element={
                  <>
                    <PageTitle title="Forgot Password" />
                    <ForgotPassword />
                  </>
                }
              />
              <Route
                path="/auth/reset-password"
                element={
                  <>
                    <PageTitle title="Reset Password" />
                    <ResetPassword />
                  </>
                }
              />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>

  );
}

export default App;
